import { useEffect, useMemo, useState } from "react";
import { Combobox, ETypeOfNotification, InputSwitch, InputTextCustom, Modal, setNotification } from "medere-ui-framework";
import { Checkbox } from "primereact/checkbox";
import { useDispatch } from "react-redux";
import { Bed, defaultBed, Establishment, LocationOption, Room, Sector } from "../../../../interface/interface";
import { getHealthServices, getMonitorTypes, loadRoomsBySectorId, saveBed } from "../../../../servicesHooks/Location/useLocationConfigService";
import { ComboChunkFilter, getComboFilteredChunk } from "../components/comboChunkFilter";
import { bedTypeOptions } from "./bedConstants";
import { isRoomBlockedSectorType } from "../../roomSectorRules";

interface CRUDBedModalProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    establishments: Establishment[];
    bedToEdit?: Bed | null;
    prefilledRoom?: Room | null;
    onSaved: () => Promise<void> | void;
}

export default function CRUDBedModal({
    visible,
    setVisible,
    establishments,
    bedToEdit = null,
    prefilledRoom = null,
    onSaved
}: Readonly<CRUDBedModalProps>) {
    const dispatch = useDispatch();
    const [formErrors, setFormErrors] = useState<{
        sector?: string;
        room?: string;
        name?: string;
        bedType?: string;
        monitorInterval?: string;
    }>({});

    const [bedItem, setBedItem] = useState<Bed>(defaultBed);
    const [roomOptions, setRoomOptions] = useState<Room[]>([]);
    const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
    const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
    const [selectedService, setSelectedService] = useState<LocationOption | null>(null);
    const [selectedBedType, setSelectedBedType] = useState<LocationOption | null>(null);
    const [selectedMonitorType, setSelectedMonitorType] = useState<LocationOption | null>(null);
    const [monitorIntervalInput, setMonitorIntervalInput] = useState<string>("");
    const [isSaving, setIsSaving] = useState(false);

    const sectors = useMemo(() => {
        const sectorMap = new Map<number, Sector>();
        (establishments ?? []).forEach((est) => {
            (est.sectors ?? []).forEach((sec) => {
                if (sec?.sector > 0 && !sectorMap.has(sec.sector)) {
                    sectorMap.set(sec.sector, sec);
                }
            });
        });
        return Array.from(sectorMap.values());
    }, [establishments]);

    const selectableSectors = useMemo(() => {
        const currentSectorId = bedToEdit?.sector ?? prefilledRoom?.sector ?? null;
        return sectors.filter((sector) => {
            if (currentSectorId != null && sector.sector === currentSectorId) return true;
            return !isRoomBlockedSectorType(sector.sectortype);
        });
    }, [bedToEdit?.sector, prefilledRoom?.sector, sectors]);

    const loadSectors = async (props: ComboChunkFilter) =>
        getComboFilteredChunk(selectableSectors, (sector) => sector.name ?? "", props);

    const loadRooms = async (props: ComboChunkFilter) =>
        getComboFilteredChunk(roomOptions, (room) => room.number ?? "", props);

    const loadBedTypes = async (props: ComboChunkFilter) =>
        getComboFilteredChunk(bedTypeOptions, (bedType) => bedType.description ?? "", props);

    const roomOptionsReloadKey = useMemo(
        () => `${selectedSector?.sector ?? -1}:${roomOptions.map((room) => `${room.room}:${room.number}`).join("|")}`,
        [roomOptions, selectedSector?.sector]
    );

    const loadRoomsForSector = async (sectorId: number, preferredRoomId?: number) => {
        const rooms = await loadRoomsBySectorId({ sectorid: sectorId });
        const normalized = rooms ?? [];
        setRoomOptions(normalized);

        if (preferredRoomId != null) {
            const selected = normalized.find((r) => r.room === preferredRoomId) ?? null;
            setSelectedRoom(selected);
            return selected;
        }

        return null;
    };

    useEffect(() => {
        if (!visible) return;

        const initialize = async () => {
            if (bedToEdit && bedToEdit.bed > 0) {
                setBedItem(bedToEdit);
                setSelectedBedType(bedTypeOptions.find((opt) => opt.id === bedToEdit.bedtype) ?? null);
                setSelectedService(
                    bedToEdit.healthservice > 0
                        ? { id: bedToEdit.healthservice, description: bedToEdit.servicename }
                        : null
                );
                setSelectedMonitorType(
                    bedToEdit.monitortype > 0
                        ? { id: bedToEdit.monitortype, description: bedToEdit.monitortypename }
                        : null
                );
                setMonitorIntervalInput(
                    bedToEdit.monitorcheckperioddefault == null ? "" : String(bedToEdit.monitorcheckperioddefault)
                );

                const sector = sectors.find((sec) => sec.sector === bedToEdit.sector) ?? null;
                setSelectedSector(sector);
                if (sector) {
                    await loadRoomsForSector(sector.sector, bedToEdit.room);
                } else {
                    setRoomOptions([]);
                    setSelectedRoom(null);
                }
                return;
            }

            if (prefilledRoom && prefilledRoom.room > 0) {
                const sector = sectors.find((sec) => sec.sector === prefilledRoom.sector) ?? null;
                setBedItem({
                    ...defaultBed,
                    sector: prefilledRoom.sector,
                    sectorname: prefilledRoom.sectorname,
                    room: prefilledRoom.room,
                    roomnumber: prefilledRoom.number,
                    building: prefilledRoom.building,
                    buildingname: prefilledRoom.buildingname,
                    buildingaddress: prefilledRoom.buildingaddress
                });
                setSelectedSector(sector);
                setSelectedBedType(null);
                setSelectedService(null);
                setSelectedMonitorType(null);
                setMonitorIntervalInput("");
                if (sector) {
                    await loadRoomsForSector(sector.sector, prefilledRoom.room);
                }
                return;
            }

            setBedItem(defaultBed);
            setSelectedSector(null);
            setSelectedRoom(null);
            setSelectedService(null);
            setSelectedBedType(null);
            setSelectedMonitorType(null);
            setRoomOptions([]);
            setMonitorIntervalInput("");
            setFormErrors({});
        };

        void initialize();
    }, [visible, bedToEdit, prefilledRoom, sectors]);

    const closeModal = () => {
        if (isSaving) return;
        setVisible(false);
    };

    const handleSave = async () => {
        if (isSaving) return;
        const errors: {
            sector?: string;
            room?: string;
            name?: string;
            bedType?: string;
            monitorInterval?: string;
        } = {};

        if (!selectedSector || selectedSector.sector <= 0) {
            errors.sector = "Este campo es requerido";
        }
        if (!selectedRoom || selectedRoom.room <= 0) {
            errors.room = "Este campo es requerido";
        }
        if (!bedItem.number?.trim()) {
            errors.name = "Este campo es requerido";
        }
        if (!selectedBedType || selectedBedType.id <= 0) {
            errors.bedType = "Este campo es requerido";
        }

        let monitorInterval: number | null = null;
        if (monitorIntervalInput.trim() !== "") {
            const parsed = Number(monitorIntervalInput);
            if (!Number.isInteger(parsed) || parsed < 0) {
                errors.monitorInterval = "Debe ser un número entero mayor o igual a cero";
            } else {
                monitorInterval = parsed;
            }
        }

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }

        if (monitorIntervalInput.trim() !== "") {
            const parsed = Number(monitorIntervalInput);
            if (!Number.isInteger(parsed) || parsed < 0) {
                return;
            }
            monitorInterval = parsed;
        }

        setIsSaving(true);
        try {
            const payload: Bed = {
                ...bedItem,
                bed: bedItem.bed > 0 ? bedItem.bed : -1,
                number: bedItem.number.trim(),
                room: selectedRoom.room,
                roomnumber: selectedRoom.number,
                sector: selectedSector.sector,
                sectorname: selectedSector.name,
                building: selectedRoom.building,
                buildingname: selectedRoom.buildingname,
                buildingaddress: selectedRoom.buildingaddress,
                healthservice: selectedService?.id ?? null,
                servicename: selectedService?.description ?? null,
                bedtype: selectedBedType.id,
                bedtypedescription: selectedBedType.description,
                monitortype: selectedMonitorType?.id ?? -1,
                monitortypename: selectedMonitorType?.description ?? "",
                monitorcheckperioddefault: monitorInterval,
            };

            await saveBed(payload);
            dispatch(setNotification({
                title: payload.bed > 0 ? "Edición exitosa" : "Creación exitosa",
                desc: payload.bed > 0 ? "Se actualizó la cama." : "Se creó la cama.",
                icon: "pi pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO
            }));

            await onSaved();
            setVisible(false);
        } catch (error: any) {
            const status = error?.response?.status;
            const message = error?.response?.data;

            if (status === 400) {
                dispatch(setNotification({ title: "No se pudo guardar", desc: String(message ?? "Faltan datos obligatorios."), icon: "pi pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
            } else if (status === 409) {
                dispatch(setNotification({ title: "No se pudo guardar", desc: "Ya existe una cama con ese nombre en la habitación seleccionada.", icon: "pi pi-exclamation-triangle", typeOfNotification: ETypeOfNotification.WARNING }));
            } else {
                dispatch(setNotification({ title: "Ocurrió un error", desc: "No se pudo guardar la cama.", icon: "pi pi-times", typeOfNotification: ETypeOfNotification.ERROR }));
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            className="location-mobile-editor-modal"
            visible={visible}
            setVisible={setVisible}
            width="w-large"
            header={<div style={{ fontSize: "20px" }}>{bedToEdit && bedToEdit.bed > 0 ? "Editar cama" : "Crear cama"}</div>}
            onHideCustom={closeModal}
            footerButtonLeftText="Cancelar"
            onClickLeftBtn={closeModal}
            footerButtonRightText={bedToEdit && bedToEdit.bed > 0 ? "Guardar" : "Crear"}
            onClickRightBtn={() => { void handleSave(); }}
        >
            <div className="location-mobile-modal-content">
                <div className="flex align-items-center gap-2 location-switch-inline-row w-fit ml-auto">
                    <InputSwitch
                        id="inputswitchbedactiveid"
                        className="w-auto"
                        checked={bedItem.active}
                        onChange={(e) => setBedItem({ ...bedItem, active: e.value })}
                    />
                    <span className="location-switch-state-label">{bedItem.active ? "Activo" : "Inactivo"}</span>
                </div>

                <div className="flex gap-2">
                    <div className="w-full">
                        <label className="block mb-2">Sector *</label>
                        <Combobox
                            className={`${formErrors.sector ? "" : "border-grey"} w-full`}
                            getItems={loadSectors}
                            optionLabel="name"
                            placeholder="Seleccionar"
                            value={selectedSector}
                            setValue={(value: Sector | null) => {
                                setFormErrors((prev) => ({ ...prev, sector: "" }));
                                setSelectedSector(value);
                                setSelectedRoom(null);
                                setRoomOptions([]);
                                setBedItem((prev) => ({
                                    ...prev,
                                    sector: value?.sector ?? -1,
                                    sectorname: value?.name ?? "",
                                    room: -1,
                                    roomnumber: ""
                                }));
                                if (value?.sector) {
                                    void loadRoomsForSector(value.sector);
                                }
                            }}
                            error={!!formErrors.sector}
                            caption={formErrors.sector}
                        />
                    </div>
                    <div className="w-full">
                        <label className="block mb-2">Habitación *</label>
                        <Combobox
                            className={`${formErrors.room ? "" : "border-grey"} w-full`}
                            getItems={loadRooms}
                            reLoadItemsValue={roomOptionsReloadKey}
                            optionLabel="number"
                            placeholder="Seleccionar"
                            value={selectedRoom}
                            setValue={(value: Room | null) => {
                                setFormErrors((prev) => ({ ...prev, room: "" }));
                                setSelectedRoom(value);
                                setBedItem((prev) => ({
                                    ...prev,
                                    room: value?.room ?? -1,
                                    roomnumber: value?.number ?? "",
                                    building: value?.building ?? prev.building,
                                    buildingname: value?.buildingname ?? prev.buildingname,
                                    buildingaddress: value?.buildingaddress ?? prev.buildingaddress
                                }));
                            }}
                            error={!!formErrors.room}
                            caption={formErrors.room}
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    <div className="w-full">
                        <label className="block mb-2">Nombre *</label>
                        <InputTextCustom
                            className={`${formErrors.name ? "" : "border-grey"} w-full`}
                            value={bedItem.number ?? ""}
                            onChange={(e) => {
                                setBedItem({ ...bedItem, number: e.target.value });
                                setFormErrors((prev) => ({ ...prev, name: "" }));
                            }}
                            error={!!formErrors.name}
                            caption={formErrors.name}
                        />
                    </div>
                    <div className="w-full">
                        <label className="block mb-2">Servicio</label>
                        <Combobox
                            className="border-grey w-full"
                            getItems={getHealthServices}
                            optionLabel="description"
                            placeholder="Seleccionar"
                            value={selectedService}
                            setValue={(value: LocationOption | null) => {
                                setSelectedService(value);
                            }}
                        />
                    </div>
                    <div className="w-full">
                        <label className="block mb-2">Tipo de cama *</label>
                        <Combobox
                            className={`${formErrors.bedType ? "" : "border-grey"} w-full`}
                            getItems={loadBedTypes}
                            optionLabel="description"
                            placeholder="Seleccionar"
                            value={selectedBedType}
                            setValue={(value: LocationOption | null) => {
                                setSelectedBedType(value);
                                setFormErrors((prev) => ({ ...prev, bedType: "" }));
                            }}
                            error={!!formErrors.bedType}
                            caption={formErrors.bedType}
                        />
                    </div>
                </div>

                <div className="location-bed-exclude-row mt-1">
                    <Checkbox
                        inputId="excludeOnStatisticsId"
                        checked={bedItem.excludeonstatistics}
                        onChange={(e) => setBedItem({ ...bedItem, excludeonstatistics: !!e.checked })}
                    />
                    <label htmlFor="excludeOnStatisticsId" className="location-bed-exclude-label" style={{marginLeft:"7px"}}>Excluir de estadísticas</label>
                </div>

                <h4 className="subtitle info-dark mt-5 mb-3">Monitor</h4>
                <div className="flex gap-2">
                    <div className="w-full">
                        <label className="block mb-2">Tipo de monitor</label>
                        <Combobox
                            className="border-grey w-full"
                            getItems={getMonitorTypes}
                            optionLabel="description"
                            placeholder="Seleccionar"
                            value={selectedMonitorType}
                            setValue={(value: LocationOption | null) => setSelectedMonitorType(value)}
                        />
                    </div>
                    <div className="w-full">
                        <label className="block mb-2">IP</label>
                        <InputTextCustom
                            className="border-grey w-full"
                            value={bedItem.monitorip ?? ""}
                            onChange={(e) => setBedItem({ ...bedItem, monitorip: e.target.value })}
                        />
                    </div>
                    <div className="w-full">
                        <label className="block mb-2">Puerto</label>
                        <InputTextCustom
                            className="border-grey w-full"
                            value={bedItem.monitorport ?? ""}
                            onChange={(e) => setBedItem({ ...bedItem, monitorport: e.target.value })}
                        />
                    </div>
                    <div className="w-full">
                        <label className="block mb-2">Intervalo de monitoreo (min)</label>
                        <InputTextCustom
                            className={`${formErrors.monitorInterval ? "" : "border-grey"} w-full`}
                            value={monitorIntervalInput}
                            onChange={(e) => {
                                setMonitorIntervalInput(e.target.value);
                                setFormErrors((prev) => ({ ...prev, monitorInterval: "" }));
                            }}
                            error={!!formErrors.monitorInterval}
                            caption={formErrors.monitorInterval}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}
